import type { ReactNode } from 'react';
import styles from './Badge.module.css';
import { cn } from '@/lib/utils';

export interface BadgeProps {
  /** Number to display. */
  count?: number;
  /** Maximum before showing "{max}+". */
  max?: number;
  /** Whether to show the badge when count is 0. */
  showZero?: boolean;
  /** Display mode. */
  variant?: 'standard' | 'dot';
  /** Badge color. */
  color?: 'primary' | 'error' | 'warning';
  /** Element the badge is attached to. */
  children: ReactNode;
}

const COLOR_MAP = {
  primary: styles.colorPrimary ?? '',
  error: styles.colorError ?? '',
  warning: styles.colorWarning ?? '',
} as const;

/**
 * Notification badge overlaid on its children.
 */
export function Badge({
  count,
  max = 99,
  showZero = false,
  variant = 'standard',
  color = 'error',
  children,
}: BadgeProps) {
  const showBadge =
    variant === 'dot' ||
    (count != null && (count > 0 || showZero));

  const displayCount =
    count != null && count > max ? `${max}+` : count;

  return (
    <span className={styles.wrapper}>
      {children}

      {showBadge && (
        <span
          className={cn(
            styles.badge,
            variant === 'dot' && styles.dot,
            COLOR_MAP[color],
          )}
          aria-label={
            variant === 'dot'
              ? 'notification'
              : `${displayCount} notifications`
          }
        >
          {variant === 'standard' && displayCount}
        </span>
      )}
    </span>
  );
}
